import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import { clearStoredPromo, getStoredPromo } from '../utils/attribution';
import './AdvancedPurchase.css';

const MONTH_LABELS = { 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' };
const rub = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

function daysWord(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'день';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
    return 'дней';
}

/**
 * Покупка и продление «Расширенного»: срок 1/3/6/12 месяцев, промокод, итоговая цена с сервера.
 * Промокод на 100% или «бесплатный доступ» включает тариф сразу, без перехода к оплате.
 */
function AdvancedPurchase({ profile, onActivated }) {
    const [options, setOptions] = useState([]);
    const [months, setMonths] = useState(1);
    const [promoOpen, setPromoOpen] = useState(false);
    const [promoInput, setPromoInput] = useState('');
    const [appliedCode, setAppliedCode] = useState('');
    const [quote, setQuote] = useState(null);
    const [checking, setChecking] = useState(false);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const personalCorporate = profile.subscription_tier === 'corporate' && !profile.company_name;
    const viaCompany = Boolean(profile.company_name);
    const hasAdvanced = (viaCompany ? profile.personal_tier : profile.subscription_tier) === 'advanced';

    useEffect(() => {
        api.get('/payments/options')
            .then((res) => setOptions(res.data.options || []))
            .catch(() => setOptions([]));
        // Промокод из ссылки (письмо, лендинг): ?promo=CODE или сохранённый ранее
        const fromUrl = new URLSearchParams(window.location.search).get('promo');
        const initial = (fromUrl || getStoredPromo() || '').trim().toUpperCase();
        if (initial) {
            setPromoOpen(true);
            setPromoInput(initial);
            setAppliedCode(initial);
        }
    }, []);

    const loadQuote = useCallback(async (code) => {
        setChecking(true);
        try {
            const res = await api.post('/payments/quote', { tariff_tier: 'advanced', months, promo_code: code || null });
            setQuote(res.data);
            setError('');
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось рассчитать стоимость'));
        } finally {
            setChecking(false);
        }
    }, [months]);

    useEffect(() => {
        if (!personalCorporate) loadQuote(appliedCode);
    }, [loadQuote, appliedCode, personalCorporate]);

    const applyPromo = (e) => {
        e.preventDefault();
        const code = promoInput.trim().toUpperCase();
        if (!code) return;
        if (code === appliedCode) loadQuote(code);
        else setAppliedCode(code);
    };

    const removePromo = () => {
        setAppliedCode('');
        setPromoInput('');
        clearStoredPromo();
    };

    const pay = async () => {
        setError('');
        setSuccess('');
        setPaying(true);
        try {
            const promo = quote?.promo_error ? null : (appliedCode || null);
            const res = await api.post('/payments/create', {
                tariff_tier: 'advanced',
                months,
                promo_code: promo,
                return_url: `${window.location.origin}/profile?payment=pending`,
            });
            if (res.data.status === 'activated') {
                clearStoredPromo();
                const until = res.data.expires_at ? new Date(res.data.expires_at).toLocaleDateString('ru-RU') : '';
                setSuccess(`Готово! Тариф «Расширенный» включён${until ? ` до ${until}` : ''}.`);
                setAppliedCode('');
                setPromoInput('');
                setPaying(false);
                onActivated?.();
                return;
            }
            clearStoredPromo();
            window.location.href = res.data.confirmation_url;
        } catch (err) {
            setError(getApiErrorMessage(err, 'Ошибка при создании платежа'));
            setPaying(false);
        }
    };

    if (personalCorporate) {
        return (
            <div className="purchase">
                <h3>Расширенный тариф</h3>
                <p className="purchase__muted">У вас корпоративный тариф — в нём уже есть всё из «Расширенного».</p>
            </div>
        );
    }

    const promoApplied = quote && quote.promo_code && !quote.promo_error;
    const showStrike = quote && quote.final < quote.full_price;
    const actionLabel = quote?.free_activation
        ? 'Активировать бесплатно'
        : `${hasAdvanced ? 'Продлить' : 'Оплатить'} ${quote ? rub(quote.final) : ''}`;

    return (
        <div className="purchase">
            <h3>Расширенный тариф</h3>
            <p className="purchase__muted">15 опросов, 5 активных одновременно, отчёты с ИИ, вопросы из зала и встраивание результатов.</p>
            {viaCompany && (
                <p className="purchase__note">Сейчас тариф выдаёт компания «{profile.company_name}». Оплата продлит ваш личный тариф — он начнёт действовать после выхода из компании.</p>
            )}

            <div className="purchase__periods" role="radiogroup" aria-label="Срок">
                {options.map((o) => (
                    <button
                        key={o.months}
                        type="button"
                        role="radio"
                        aria-checked={months === o.months}
                        className={`purchase__period${months === o.months ? ' is-active' : ''}`}
                        onClick={() => setMonths(o.months)}
                    >
                        <span className="purchase__period-title">{MONTH_LABELS[o.months] || `${o.months} мес.`}</span>
                        <span className="purchase__period-price">{rub(o.price)}</span>
                        {o.discount_pct > 0
                            ? <span className="purchase__period-badge">−{o.discount_pct}% · {rub(o.per_month)}/мес</span>
                            : <span className="purchase__period-sub">{rub(o.per_month)}/мес</span>}
                    </button>
                ))}
            </div>

            <div className="purchase__promo">
                {!promoOpen ? (
                    <button type="button" className="purchase__link" onClick={() => setPromoOpen(true)}>Есть промокод?</button>
                ) : (
                    <form className="purchase__promo-form" onSubmit={applyPromo}>
                        <input
                            className="form-input"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                            placeholder="Промокод"
                            aria-label="Промокод"
                            autoComplete="off"
                        />
                        <button type="submit" className="btn btn--secondary" disabled={checking || !promoInput.trim()}>Применить</button>
                        {appliedCode && (
                            <button type="button" className="purchase__link" onClick={removePromo}>Убрать</button>
                        )}
                    </form>
                )}
                {quote?.promo_error && appliedCode && (
                    <div className="purchase__promo-msg purchase__promo-msg--error" role="alert">{quote.promo_error}</div>
                )}
                {promoApplied && (
                    <div className="purchase__promo-msg purchase__promo-msg--ok" role="status">Промокод {quote.promo_code}: {quote.promo_label}</div>
                )}
            </div>

            {quote && !quote.error && (
                <div className="purchase__summary">
                    <div className="purchase__price">
                        {showStrike && <s>{rub(quote.full_price)}</s>}
                        <strong>{quote.free_activation ? 'Бесплатно' : rub(quote.final)}</strong>
                    </div>
                    <div className="purchase__days">
                        {quote.days} {daysWord(quote.days)}
                        {quote.bonus_days > 0 && <> + <b>{quote.bonus_days} {daysWord(quote.bonus_days)} в подарок</b></>}
                        {hasAdvanced && ' — добавится к текущему сроку'}
                    </div>
                </div>
            )}

            {error && <div className="profile-feedback profile-feedback--error">{error}</div>}
            {quote?.error && <div className="profile-feedback profile-feedback--error">{quote.error}</div>}
            {success && <div className="profile-feedback profile-feedback--success">{success}</div>}

            <button
                type="button"
                className="btn btn--primary purchase__pay"
                onClick={pay}
                disabled={paying || checking || !quote || Boolean(quote.error)}
            >
                {paying ? 'Переходим к оплате…' : actionLabel}
            </button>
        </div>
    );
}

export default AdvancedPurchase;
